import { FC, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { DTOQualityUserBadge } from 'src/apis/dto/DTOQualityUserBadge';
import ModalLayoutBase, { MobileModalType, ModalContent as ModalContentBase } from 'src/components/ModalLayout';
import BadgeImageBase from 'src/components/QualityUserBadge/BadgeImage';
import { useGetQualityUserBadgeDetailsQuery } from 'src/hooks/useQualityUserBadgeQuery';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { showCommonErrorToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import HelpIconWithTooltip from './HelpIconWithTooltip';
import QualityUserBadgeProgress from './Progress';
const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
`;
const ModalContent = styled(ModalContentBase)`
  padding: 24px;
  padding-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({
  theme
}) => theme.screenSize.mobile} {
    padding-top: 36px;
    padding-bottom: 16px;
  }
`;
const BadgeImage = styled(BadgeImageBase)`
  width: 86px;
  height: 86px;
`;
const BadgeTitle = styled.p`
  margin-top: 16px;
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
  line-height: 21.48px;
  text-align: center;
  color: ${({
  theme
}) => theme.color.content12};
`;
const BadgeDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 16.71px;
  text-align: center;
  color: ${({
  theme
}) => theme.color.content12};
  margin-bottom: 12px;
`;
const Card = styled.div`
  background-color: ${({
  theme
}) => theme.color.surface1};
  border-radius: 24px;
  padding: 16px 24px;
  margin-top: 12px;
  width: 100%;
`;
const CardHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: 4px;
  color: ${({
  theme
}) => theme.color.content9};
`;
const CardTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  line-height: 16.71px;
  text-align: ${({
  theme
}) => theme.isRtl ? 'right' : 'left'};
`;
interface Props {
  qualityUserBadge: DTOQualityUserBadge;
  isMine: boolean;
}
const QualityUserBadgeModal: FC<Props> = ({
  qualityUserBadge,
  isMine
}) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const {
    data,
    isSuccess,
    isError
  } = useGetQualityUserBadgeDetailsQuery({
    isMine
  });
  const currentBadgeStateInfo = useMemo(() => {
    return (data?.data?.states || []).find(state => state.state === qualityUserBadge.state);
  }, [data?.data?.states, qualityUserBadge.state]);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.LOG,
      eventName: isMine ? EVENT_NAME.MY_PROFILE__IMPRESSION_AZAR_BADGE_MODAL : EVENT_NAME.PROFILE__IMPRESSION_AZAR_BADGE_MODAL,
      eventParams: {
        action_category: 'impression',
        tab: 'anywhere',
        page: isMine ? 'my_profile' : 'profile',
        target: 'azar_badge_modal',
        badge_status: qualityUserBadge.state
      }
    });
  }, [isMine, qualityUserBadge.state]);
  useEffect(() => {
    if (isError) {
      showCommonErrorToast();
      closeModal(ModalType.QUALITY_USER_BADGE);
    }
  }, [isError, showCommonErrorToast, closeModal]);
  if (!isSuccess) return null;
  return <ModalLayout isDim mobileType={MobileModalType.BOTTOM_SHEET} data-sentry-element="ModalLayout" data-sentry-component="QualityUserBadgeModal" data-sentry-source-file="index.tsx">
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <BadgeImage src={currentBadgeStateInfo?.headerImageUrl} showFallback={true} data-sentry-element="BadgeImage" data-sentry-source-file="index.tsx" />
        <BadgeTitle data-sentry-element="BadgeTitle" data-sentry-source-file="index.tsx">{currentBadgeStateInfo?.headerTitle}</BadgeTitle>
        <BadgeDescription data-sentry-element="BadgeDescription" data-sentry-source-file="index.tsx">{currentBadgeStateInfo?.headerContent}</BadgeDescription>
        <Card data-sentry-element="Card" data-sentry-source-file="index.tsx">
          <CardHeader data-sentry-element="CardHeader" data-sentry-source-file="index.tsx">
            <CardTitle data-sentry-element="CardTitle" data-sentry-source-file="index.tsx">{t('badge_progress_title')}</CardTitle>
            <HelpIconWithTooltip tooltipMessage={t('badge_progress_tooltip')} data-sentry-element="HelpIconWithTooltip" data-sentry-source-file="index.tsx" />
          </CardHeader>
          <QualityUserBadgeProgress qualityUserBadge={qualityUserBadge} data-sentry-element="QualityUserBadgeProgress" data-sentry-source-file="index.tsx" />
        </Card>
      </ModalContent>
    </ModalLayout>;
};
export default QualityUserBadgeModal;